// File: components/engine/systems/collision.ts
import { COLORS } from "../../config/colors";
import { CANVAS_WIDTH, CANVAS_HEIGHT, PLAYER_HEIGHT, PLAYER_WIDTH, SWORD_RANGE } from "../../config/dimensions";
import { GameInstance } from "../GameInstance";
import { spawnParticles } from "./spawner";

export const handleCollisions = (game: GameInstance) => {
  // Handle player projectiles vs enemies
  for (const p of game.projectiles) {
    p.x += p.vx;
    p.y += p.vy;

    // Heart Projectile Logic (Sine Wave)
    if (p.type === 'HEART' && p.active) {
      if (p.time === undefined) p.time = 0;
      p.time += 0.1;
      // Add lateral sine movement perpendicular to velocity
      p.x += Math.sin(p.time) * 3;
    }

    if (p.x < 0 || p.x > CANVAS_WIDTH) p.vx *= -1;

    if (!p.active) continue;

    // Deflect Logic
    const isHarmful = p.type !== "LIGHT" && p.type !== "GOLD"; // Enemy bullets are harmful

    if (game.player.isSwinging && isHarmful) {
      const dx = (game.player.x + PLAYER_WIDTH / 2) - p.x;
      const dy = (game.player.y + PLAYER_HEIGHT / 2) - p.y;
      if (Math.sqrt(dx * dx + dy * dy) < SWORD_RANGE) {
        const perfect = Math.sqrt(dx * dx + dy * dy) < 40;

        // Transform the bullet based on deflection
        p.type = game.goldenHonmoonTimer > 0 ? "GOLD" : (perfect ? "GOLD" : "LIGHT");

        p.vy = -Math.abs(p.vy) * (perfect ? 1.5 : 1.2);
        p.vx = (p.x - (game.player.x + PLAYER_WIDTH / 2)) * 0.3;

        game.combo++;
        game.shake = 5;
        spawnParticles(game, p.x, p.y, p.type === "GOLD" ? COLORS.projectileLight : COLORS.sword, 5);

        if (game.combo >= 15 && game.goldenHonmoonTimer === 0) {
          game.goldenHonmoonTimer = 300;
          game.achievedGoldenHonmoonThisRun = true;
          game.callbacks.onUpdateUI(game.score, game.honmoon, game.wave, game.goldenHonmoonTimer);
        }
      }
    }

    // Hit Squad (Player or Allies)
    if (isHarmful) {
      const hitPlayer = p.x > game.player.x && p.x < game.player.x + PLAYER_WIDTH && p.y > game.player.y && p.y < game.player.y + PLAYER_HEIGHT;
      const hitZoey = p.x > game.zoey.x && p.x < game.zoey.x + game.zoey.width && p.y > game.zoey.y && p.y < game.zoey.y + game.zoey.height;
      const hitMira = p.x > game.mira.x && p.x < game.mira.x + game.mira.width && p.y > game.mira.y && p.y < game.mira.y + game.mira.height;
      const hitSquad = hitPlayer || hitZoey || hitMira;

      const offScreen = p.y > CANVAS_HEIGHT;
      if (hitSquad || offScreen) {
        p.active = false;

        let damage = 5;
        if (hitSquad) {
          if (p.type === 'SONIC') damage = 15;
          if (p.type === 'BEAM') damage = 20;
          if (p.type === 'HEART') damage = 8;
          if (p.type === 'DARK') damage = 10;
        }

        game.honmoon = Math.max(0, game.honmoon - damage);
        game.shake = hitSquad ? damage : 0;
        game.combo = 0;

        if (hitSquad) spawnParticles(game, p.x, p.y, "#ff0000", 10);
        game.callbacks.onUpdateUI(game.score, game.honmoon, game.wave, game.goldenHonmoonTimer);
        if (game.honmoon <= 0 && !game.isGameOver) {
          // FIX: Don't set isGameOver here. Let the callback in Stage.tsx handle it.
          // This allows the ward revival logic to correctly intervene without halting the game loop.
          // game.isGameOver = true; 
          game.callbacks.onGameOver(game.score);
        }
      }
    }
    // Hit Enemy (Friendly Fire / Deflected)
    else if (p.active) {
      let hitEnemy = false;
      for (const e of game.enemies) {
        if (p.x > e.x && p.x < e.x + e.width && p.y > e.y && p.y < e.y + e.height) {
          p.active = false; hitEnemy = true;
          e.hp -= (p.type === "GOLD" ? 3 : 1);
          e.flashTimer = 5;
          spawnParticles(game, p.x, p.y, COLORS.honmoon, 8);
          game.score += (p.type === "GOLD" ? 300 : 100);
          if (e.hp <= 0) game.enemyDefeated(e);
          game.callbacks.onUpdateUI(game.score, game.honmoon, game.wave, game.goldenHonmoonTimer);
          break;
        }
      }
      if (p.y < 0 && !hitEnemy) p.active = false;
    }
  }
  game.projectiles = game.projectiles.filter(p => p.active);

  // Handle ally projectiles vs enemies AND enemy projectiles
  for (let i = game.allyProjectiles.length - 1; i >= 0; i--) {
    const p = game.allyProjectiles[i];

    // 1. Check for Projectile Interception (Defense)
    for (const proj of game.projectiles) {
      if (proj.type !== 'LIGHT' && proj.type !== 'GOLD' && proj.active) {
        const dx = p.x + p.width / 2 - proj.x;
        const dy = p.y + p.height / 2 - proj.y;
        if (Math.sqrt(dx * dx + dy * dy) < (p.width + p.height) / 3) {
          proj.active = false; // Destroy bullet
          spawnParticles(game, proj.x, proj.y, '#ffffff', 3);
          if (p.type === 'ZOEY_MISSILE') {
            p.life = 0;
            break;
          }
        }
      }
    }

    // 2. Check for Enemy Hits
    for (const e of game.enemies) {
      if (p.x < e.x + e.width && p.x + p.width > e.x && p.y < e.y + e.height && p.y + p.height > e.y) {
        e.hp -= 2;
        e.flashTimer = 5;
        spawnParticles(game, e.x + e.width / 2, e.y + e.height / 2, p.color, 10);
        game.score += 50;
        if (e.hp <= 0) game.enemyDefeated(e);

        if (p.type === 'ZOEY_MISSILE') {
          game.allyProjectiles.splice(i, 1);
        }
        game.callbacks.onUpdateUI(game.score, game.honmoon, game.wave, game.goldenHonmoonTimer);
        break;
      }
    }
  }
};